<template>
  <div
    ref="widget"
    :class="['authx-widget', { fadeIn: setShow, preview: previewMode }]"
  >
    <div v-if="previewMode" class="authx-widget--preview-mode">
      <div class="tag">
        <span>Preview Mode</span>
      </div>
      <div class="close" @click="$emit('close')">
        <i class="ra-icon--x" />
      </div>
    </div>
    <div class="authx-form fadeInUp">
      <div v-if="loading" v-loading="loading" style="height: 100px"></div>
      <template v-else>
        <div v-if="widgetData.customization" class="authx-form--header">
          <div class="authx-form--logo">
            <img
              v-if="widgetData.customization.logo"
              :src="widgetData.customization.logo"
              alt=""
            />
            <h6 class="company-name">
              Welcome to
              {{
                widgetData.customization && widgetData.customization.company
              }}!
            </h6>
          </div>
        </div>
        <div class="authx-form--body">
          <form-signup
            v-if="view === 'signup'"
            v-bind="formData"
            @next="setNext"
            @navigate="setView"
          />
          <form-login
            v-else-if="view === 'login'"
            v-bind="formData"
            @next="setNext"
            @navigate="setView"
          />
          <form-forgot-password
            v-else-if="view === 'forgot-password'"
            :domain-data="domainData"
            :validation="validation"
            :colors="colors"
            @navigate="setView"
          />
          <form-verify-otp
            v-else-if="view === 'otp-verification'"
            v-bind="{ ...otp, domainData: domainData, colors: colors }"
            @next="setNext"
            @navigate="setView"
          />
        </div>
      </template>
    </div>
    <div class="authx-widget--secured">
      <p>
        Secured with
        <a href="https://radius.africa/" target="_blank">Radius</a>
      </p>
    </div>
  </div>
</template>

<script>
import { currentUser } from "@/lib/utils";
import FormSignup from "@/components/Products/AuthX/Widget/Forms/FormSignup.vue";
import FormLogin from "@/components/Products/AuthX/Widget/Forms/FormLogin.vue";
import FormForgotPassword from "@/components/Products/AuthX/Widget/Forms/FormForgotPassword.vue";
import FormVerifyOtp from "@/components/Products/AuthX/Widget/Forms/FormVerifyOtp.vue";

import authxMixin from "@/mixins/authx";

export default {
  name: "AuthXWidget",
  mixins: [authxMixin],
  components: {
    FormVerifyOtp,
    FormForgotPassword,
    FormLogin,
    FormSignup
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    previewMode: {
      type: Boolean,
      default: true
    },
    widgetData: {
      type: Object,
      required: true
    },
    domainData: {
      type: Object,
      default() {
        return {};
      }
    },
    googleAuth: {
      type: [Boolean, Number],
      default: 0
    },
    facebookAuth: {
      type: [Boolean, Number],
      default: 0
    },
    fieldTypes: {
      type: [Object, Array],
      default() {
        return [];
      }
    },
    socialAuth: {
      type: Boolean,
      default: false
    },
    userData: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      view: "login",
      radiusUser: currentUser(),
      otp: {
        title: "Please verify your identity",
        identifier: "",
        token: ""
      },
      data: null
    };
  },

  provide() {
    return {
      preview: this.previewMode
    };
  },

  computed: {
    setShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      }
    },
    setLoading: {
      get() {
        return this.loading;
      },
      set(value) {
        this.$emit("update:loading", value);
      }
    },
    formData() {
      const { schema, mfa } = this.widgetData;

      let formData = {
        fieldTypes: this.fieldTypes,
        googleAuth: this.googleAuth,
        facebookAuth: this.facebookAuth,
        domainData: this.domainData,
        colors: this.colors
      };

      if (this.view === "login" || this.view === "forgot-password") {
        formData["schema"] = schema && schema.login;
        formData["mfa"] = mfa && mfa.login;
      }

      if (this.view === "signup") {
        formData["schema"] = schema && schema.signup;
        formData["mfa"] = mfa && mfa.signup;
        formData["socialAuth"] = this.socialAuth;
        formData["userData"] = this.userData;
      }

      return formData;
    },
    colors() {
      return (
        this.widgetData &&
        this.widgetData.customization &&
        this.widgetData.customization.colors
      );
    },
    validation() {
      const passwordData =
        this.formData &&
        this.formData.schema &&
        this.formData.schema.filter(data => {
          return data.is_password_type;
        });

      return passwordData.length ? passwordData[0].password_validation : {};
    }
  },
  watch: {
    setLoading() {
      if (!this.loading) {
        setTimeout(() => {
          this.colors && this.setPrimaryColour(this.colors);
        });
      }
    },
    view() {
      setTimeout(() => {
        this.colors && this.setPrimaryColour(this.colors);
      });
    }
  },

  mounted() {
    // document.body.style.position = 'relative';
    const el = this.$refs.widget;
    document.querySelector("body").append(el);
  },

  created() {
    this.gaTrack();
  },

  methods: {
    setView(view) {
      this.view = view;
    },
    setNext(data) {
      const { action } = data;

      switch (action) {
        case "oauth_redirect":
          this.redirectApp(data);
          break;
        case "otp_verification":
          this.otp = {
            ...this.otp,
            ...data
          };
          this.setView("otp-verification");
          break;
        default:
          this.redirectApp(data);
      }
    },
    redirectApp(data) {
      this.notification("Logged In", "User logged in successfully", "success");
      let { redirect_uri } = data;
      if (this.domainData.authxState) {
        redirect_uri += `&__authx_state=${this.domainData.authxState}`;
      }
      window.location = redirect_uri;
    },

    gaTrack() {
      this.$ga.event({
        eventCategory: "AuthX Widget",
        eventAction: `Widget Open`
      });
    },

    close() {
      // destroy the vue listeners, etc
      this.$destroy();

      // remove the element from the DOM
      this.$el.parentNode.removeChild(this.$el);
    }
  }
};
</script>

<style lang="scss">
.authx-widget {
  width: 100%;
  height: 100%;
  min-height: 100vh;
  padding-bottom: 150px;
  overflow-y: scroll;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  background: var(--ra-white-bg);

  &.preview {
    background: rgba(0, 0, 0, 0.8);

    .authx-widget--secured {
      p,
      a {
        color: var(--ra-white) !important;
      }
    }
  }

  &--preview-mode {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 20px 0;

    .tag {
      span {
        border-radius: 100px;
        background: var(--ra-grey-tertiary);
        padding: 8px 15px 8px 25px;
        color: var(--ra-white);
        font-size: 0.825rem;
        font-weight: 600;
        position: relative;

        &::before {
          position: absolute;
          content: "";
          height: 6px;
          width: 6px;
          border-radius: 10px;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: var(--ra-white);
        }
      }
    }

    .close {
      cursor: pointer;
      height: 26px;
      width: 26px;
      padding: 5px;
      border-radius: 100px;
      background: var(--ra-white);
      display: flex;
      justify-content: center;
      align-items: center;
      -webkit-box-shadow: 0 0 80px 0 rgb(77 84 124 / 9%);
      box-shadow: 0 0 80px 0 rgb(77 84 124 / 9%);

      i {
        color: var(--ra-grey-tertiary);
        font-size: 0.75rem;
      }
    }
  }

  .authx-form {
    position: relative;
    top: 10vh;
    margin: 0 auto;
    background: var(--ra-white);
    height: auto;
    max-width: 450px;
    padding: 30px;
    border-radius: 12px;
    -webkit-box-shadow: 0 0 80px 0 rgb(77 84 124 / 9%);
    box-shadow: 0 0 80px 0 rgb(77 84 124 / 9%);

    &--header {
      padding: 20px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      justify-content: center;

      .company-name {
        font-size: 1.125rem;
      }

      .authx-form--logo {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        img {
          height: 42px;
          width: auto;
          margin-bottom: 8px;
        }
      }
    }

    &--nav {
      margin-bottom: 20px;
    }

    &--body {
      height: auto;
    }

    &--footer {
      //
    }
  }

  &--secured {
    position: fixed;
    bottom: 30px;
    left: 50px;
    opacity: 0.8;
    display: flex;
    align-items: center;
    justify-content: center;

    p,
    a {
      font-size: 0.75rem;
      color: var(--ra-grey-primary) !important;
    }
  }

  .authx-input .el-input__inner {
    border-color: var(primaryColor);
  }

  .el-loading-spinner .path {
    stroke: #d7d7d7 !important;
  }
}

@media (max-width: 1024px) {
  .authx-widget .authx-form {
    max-width: 400px;
  }
}

@media (max-width: 600px) {
  .authx-widget {
    background: var(--ra-white) !important;
    padding: 10px 10px 100px;

    .authx-form {
      padding: 40px 20px;
    }
  }
}
</style>
